import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DataGrid, {Column, DataGridRef, Editing, Grouping, Item, Lookup, MasterDetail, Paging, RowDragging, Scrolling, Toolbar} from "devextreme-react/data-grid";
import reservationDetailView from "./reservation-detail-view";
import CustomStore from "devextreme/data/custom_store";
import IReservationDetailSommaire, {ReservationDetailSommaireTypeAuxiliaire} from "../../models/IReservationDetailSommaire";
import {add, differenceInDays, differenceInHours} from "date-fns";
import IGestionReservation from "../../models/IGestionReservation";
import DataSource from "devextreme/data/data_source";
import GestionReservaitonTrancheAgePopup from "./gestion-reservaiton-tranche-age-popup";
import IUnite from "../../models/IUnite";
import Guid from "../../models/Guid";
import {TypeTabCalendrier} from "../calendrier/calendrier";
import useApi, {IApiErreur} from "../../hooks/useApi";
import {FocusedRowChangedEvent, InitNewRowEvent} from "devextreme/ui/data_grid";
import {IonIcon} from "@ionic/react";
import useLookUpBase from "../../components/selectbox/LookupBase";
import IReservationList from "../../models/IReservationList";
import IDataSource from "../../models/IDataSource";
import ILookupItem from "../../models/ILookupItem";
import {useAuth} from "../../contexts/auth";
import PopupEquipement from "./popup-equipement";
import IReservationEquipement from "../../models/IReservationEquipement";
import useClient from "../../components/selectbox/client";
import PopupPage from "../../components/popups/PopupPage";
import IClient from "../../models/IClient";
import PopUpOccupant from "./popup-occupant";
import IReservationDetailOccupant from "../../models/IReservationDetailOccupant";
import {ICustomRule, Mode} from "../../hooks/useCustomDataSource";
import GestionCatalogueProduits from "../gestion-catalogue-produits/gestion-catalogue-produits";
import {useStorage} from "../../hooks/useStorage";
import {banOutline} from "ionicons/icons";
import {Button} from "devextreme-react";
import {ReservationDetailTypeDetail} from "../../models/ReservationDetailTypeDetail";
import MessageBox from "../../components/popups/MessageBox";
import ICatalogue from "../../models/ICatalogue";
import PopupOptions from "./popup-options";
import IOptionReservation, {OptionReservationModeCalcul} from "../../models/OptionReservation";
import {TypeAuxiliaire} from "../../models/TypeAuxiliaire";

interface IReservationDetailSommaireGridProps {
    reservationFormDataSource: IDataSource<IReservationList, IGestionReservation>,

}

const ReservationDetailSommaireGrid = ({reservationFormDataSource}: IReservationDetailSommaireGridProps) => {
    const {user} = useAuth();

    const dataGridRef = useRef<DataGridRef>(null)
    const {getData: getUnitesDisponibles} = useApi<ILookupItem[]>("typeunite")
    const {data: unites} = useLookUpBase({controllerName: "unite"})
    const {data: typeUnites} = useLookUpBase({controllerName: "typeunite"});
    const {data: catalogues, editorOptions: catalogueEditorOptions, PopupGestion: CataloguePopGestion} = useLookUpBase({controllerName: "catalogue", showClear: false})
    const {postData: postReservationDetailSommaire, fetchData, data: reservationDetailSommaire} = useApi<IReservationDetailSommaire>();
    const [focusedRow, setFocusedRow] = useState<IReservationDetailSommaire>()
    const [isPopupEquipementOuvert, setIsPopupEquipementOuvert] = useState(false)
    const {PopupGestionClient, clientEditorOptions, ClientSelectBox} = useClient();
    const [isPopupVisibleOccupant, setPopupVisibilityOccupant] = useState(false);
    const [isPopupVisibleOptions, setIsPopupVisibleOptions] = useState<{ visible: boolean, optionReservation?: IOptionReservation[], dateDebut?: Date, dateFin?: Date }>({visible: false, optionReservation: []})

    const {postData: postCalculerReservation} = useApi<IGestionReservation>();
    const {
        selectedDataRow: selectedReservation,
        mode,
        setIsFieldModifier,
        setSelectedDataRow,
        dataSource
    } = reservationFormDataSource;
    const [validationRules, setValidationRules, validationRulesIsExpired] = useStorage<ICustomRule[]>({key: "validationRules-reservationDetailSommaire", storageType: "localStorage", defaultValue: [], expirationInMilliseconds: 1000 * 60 * 5});

    const [isMessageBoxOpen, setIsMessageBoxOpen] = useState<IReservationDetailSommaire | undefined>(undefined);
    const [messageBoxConfirmCallBack, setMessageBoxConfirmCallBack] = useState<() => void>(() => {
    })

    useEffect(() => {
        const abortController = new AbortController();
        if (validationRulesIsExpired || validationRules.length == 0) {
            fetchData("get", {customPath: `reservationDetailSommaire/getValidation`, returnOnly: true, abortSignal: abortController.signal}).then((result: string[]) => {
                if (result) {
                    setValidationRules(result.map(fieldName => {
                        return {type: "async", message: "", reevaluate: true, propertyName: fieldName} as ICustomRule
                    }))
                    dataGridRef.current?.instance()?.refresh()
                }
            })
        }
    }, [dataGridRef, validationRulesIsExpired])

    const validate = useCallback(async (data: IReservationDetailSommaire | undefined) => {
        const result = await fetchData("post", {
            customPath: `reservationDetailSommaire/ValidateModel`,
            body: data as unknown as object,
            returnOnly: true
        });
        return result as unknown[] as IApiErreur[];
    }, [fetchData]);

    const validationCallback = useCallback((e: any) => {
        return new Promise(async (resolve, reject) => {
            if (mode === Mode.Lecture) return resolve(undefined);
            try {

                const result: IApiErreur[] = await validate(e.data);
                const erreur = result.find(f => f.propertyName.toLowerCase() === e.rule.propertyName.toLowerCase())
                if (erreur) {
                    reject(erreur.errorMessage);
                }
            } catch (ex) {
                resolve(undefined);
            }
            resolve(undefined);
        })
    }, [mode, validate])

    const customizeColumns = useCallback((items: any) => {
        if (mode === Mode.Lecture || !validationRules || validationRules.length === 0) return;

        items.forEach((col: any) => {
            if (col.dataField) {
                const rule = validationRules.find(f => f.propertyName.toLowerCase() === col.dataField?.toLowerCase());
                if (rule) {
                    rule.validationCallback = validationCallback;
                    col.validationRules = [rule];
                }
            }
        });

    }, [validationRules, mode, validationCallback])

    const ajouterButtonOtions = useMemo(() => ({
        icon: 'plus',
        text: "Ajouter détail",
        disabled: mode === Mode.Lecture,
        onClick: () => {
            dataGridRef?.current?.instance().addRow()
        }

    }), [mode])

    const effacerButtonOtions = useMemo(() => ({
        icon: 'remove',
        text: "Effacer",
        disabled: mode === Mode.Lecture,
    }), [mode])

    const occupantsCount = useMemo(() => {
            return focusedRow
                && focusedRow.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite
                && focusedRow.reservationDetailOccupants
                && focusedRow.reservationDetailOccupants.reduce((count, occupant) => {
                    if (occupant.dateSuppression == null)
                        count += 1;
                    return count
                }, 0)

        }
        , [focusedRow]);
    const occupantsButtonOtions = useMemo(() => ({
        icon: 'group',
        text: `Occupants${occupantsCount || 0 > 0 ? `(${occupantsCount})` : ''}`,
        onClick: () => setPopupVisibilityOccupant(true),
    }), [occupantsCount])

    const optionButtonOtions = useMemo(() => ({
        icon: 'bulletlist',
        text: `Options`,
        onClick: () => setIsPopupVisibleOptions({visible: (focusedRow?.optionReservations?.length || 0) > 0, optionReservation: focusedRow?.optionReservations, dateDebut: focusedRow?.dateDebut, dateFin: focusedRow?.dateFin}),
    }), [focusedRow?.optionReservations, focusedRow?.dateDebut, focusedRow?.dateFin])

    const bloquerButtonOtions = useMemo(() => ({
        icon: "icon ban-outline",
        text: "Bloquer",
        disabled: mode === Mode.Lecture,
    }), [mode])

    const hasEquipementSelected = useMemo(() => {
        return focusedRow
            && focusedRow.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite
            && focusedRow.reservationDetailEquipements
            && focusedRow.reservationDetailEquipements.idUniteEquipementType
            && !focusedRow.reservationDetailEquipements.idUniteEquipementType.equals(Guid.empty)
    }, [focusedRow]);

    const equipementButtonOtions = useMemo(() => ({
        text: `Équipement ${hasEquipementSelected ? '(1)' : ''}`,
        onClick: () => {
            setIsPopupEquipementOuvert(true)
        }
    }), [setIsPopupEquipementOuvert, hasEquipementSelected])

    const uniteProduitCellComponent = useCallback((e: any) => {
        const typeAuxiliaire = e.data.typeAuxiliaire || e.data.data.typeAuxiliaire;
        const data = typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite ? (unites || []).find(f => f.id.equals(e.data.value)) : (catalogues || []).find(f => f.id.equals(e.data.value))
        return <div title={data?.description}>
            {data?.description}
        </div>
    }, [unites, catalogues])

    const recalculeReservationSommaire = useCallback((currentRow: IReservationDetailSommaire, forceRecalcul: boolean) => {

        postReservationDetailSommaire({
            ReservationDetailSommaire: forceRecalcul ? {...currentRow, reservationDetails: currentRow.reservationDetails.map(r => ({...r, type: r.type === ReservationDetailTypeDetail.Modifier ? ReservationDetailTypeDetail.Automatique : r.type}))} : currentRow,
            IdUsager: user?.idUsager
        }, 'reservationDetailSommaire/CreateOrUpdateRow', true).then((newRow) => {
            const result = {...currentRow, ...newRow};
            postCalculerReservation({
                ...selectedReservation,
                reservationDetailSommaires: [...selectedReservation?.reservationDetailSommaires.filter(f => !f.idReservationDetailSommaire?.equals(currentRow.idReservationDetailSommaire)) || [], result],
            }, "reservation/CalculerTotal", true)
                .then((reservation) => {
                    setSelectedDataRow(reservation)
                })
            setIsFieldModifier(true);
        });
    }, [postReservationDetailSommaire, user?.idUsager, postCalculerReservation, selectedReservation, setSelectedDataRow, setIsFieldModifier]);

    const dataGridStore = useMemo(() => new CustomStore({
        key: "idReservationDetailSommaire",
        byKey: async (key, extraOptions) => {
            return selectedReservation.reservationDetailSommaires.find(f => f.idReservationDetailSommaire?.equals(key)) || {} as IReservationDetailSommaire
        },
        load: async () => {
            const detailSommaires = (selectedReservation?.reservationDetailSommaires || [] as IReservationDetailSommaire[]).map((reservation) => {
                let nbJours: number | "" = "";

                if (reservation.typeAuxiliaire == ReservationDetailSommaireTypeAuxiliaire.Unite) {
                    if (reservation.reservationDetails) {
                        if (reservation.dateFin && reservation.dateDebut) {
                            const diffHours = differenceInHours(reservation.dateFin, reservation.dateDebut);
                            nbJours = Math.round(diffHours / 24) | 0;
                        }
                    }
                } else {
                    nbJours = "";
                }

                return {...reservation, idReservationDetailSommaire: reservation.idReservationDetailSommaire || Guid.empty, nombreJours: nbJours} as IReservationDetailSommaire;
            });
            return {data: detailSommaires, totalCount: detailSommaires.length};
        },
        insert: async (values: IReservationDetailSommaire) => {
            setIsFieldModifier(true);
            return postReservationDetailSommaire({
                ReservationDetailSommaire: {...values, idReservationEntete: selectedReservation?.idReservationEntete} as IReservationDetailSommaire,
                IdUsager: user?.idUsager
            }, 'reservationDetailSommaire/CreateOrUpdateRow', true).then((newRow) => {
                const result = {...values, ...newRow};
                postCalculerReservation({
                    ...selectedReservation,
                    reservationDetailSommaires: [...selectedReservation?.reservationDetailSommaires || [], result],
                }, "reservation/CalculerTotal", true)
                    .then((reservation) => {
                        setSelectedDataRow(reservation)
                    })
                setSelectedDataRow((prev) => {
                    return {
                        ...prev,
                        reservationDetailSommaires: [...prev?.reservationDetailSommaires || [], result],
                    } as IGestionReservation
                })
                setIsFieldModifier(true)
                return result;
            });
        },
        update: async (key: any, newValues: any) => {
            const values: IReservationDetailSommaire = newValues.detailOnly !== undefined ? newValues.reservationDetailSommaire : newValues;
            let currentRow = selectedReservation.reservationDetailSommaires.find(f => f.idReservationDetailSommaire?.equals(key)) || {} as IReservationDetailSommaire
            currentRow = {...currentRow, ...values};

            if (currentRow.dateDebut) {
                if (currentRow.nombreJours && values?.nombreJours) {
                    currentRow.dateFin = add(currentRow.dateDebut, {days: values?.nombreJours});
                }
            } else {
                currentRow.nombreJours = 0;
            }

            if (!newValues.detailOnly && currentRow.reservationDetails.some(s => s.type === ReservationDetailTypeDetail.Modifier)) {
                setIsMessageBoxOpen(currentRow)
            } else {
                recalculeReservationSommaire(currentRow, false);
            }
        },
        remove: async (key: any) => {
            setIsFieldModifier(true);
            setSelectedDataRow((prev) => {
                let detailEffacer = prev?.reservationDetailSommaires.filter(f => f.idReservationDetailSommaire?.equals(key)).map(reservation => {
                    return {...reservation, dateSuppression: new Date(), idUsagerSuppression: user?.idUsager, isDeleted: true}
                });

                return {
                    ...prev,
                    reservationDetailSommaires: [...prev.reservationDetailSommaires.filter(f => !f.idReservationDetailSommaire?.equals(key)), ...detailEffacer]
                }
            })
        }
    }), [selectedReservation, postReservationDetailSommaire, setIsFieldModifier, setSelectedDataRow, selectedReservation.idReservationEntete, user?.idUsager, postCalculerReservation, recalculeReservationSommaire])

    const dataGridDetailDataSource = useMemo(() => new DataSource({
        key: "idReservationDetailSommaire",
        store: dataGridStore,
        reshapeOnPush: true
    }), [dataGridStore]);

    const adulteCellRender = useCallback(({data, adulte}: {
        data: IReservationDetailSommaire,
        adulte: boolean
    }) => {
        const {typeAuxiliaire, idReservationDetailSommaire, reservationAges} = data;
        if (typeAuxiliaire !== ReservationDetailSommaireTypeAuxiliaire.Unite) return;

        return <GestionReservaitonTrancheAgePopup id={idReservationDetailSommaire} adulte={adulte} onTrancheAgeValueChanged={() => {
            if (mode !== Mode.Lecture) {
                dataGridRef.current?.instance().saveEditData();
                //recalculeReservationSommaire(data, false);
                dataGridStore.update(idReservationDetailSommaire, data)
            }
        }} trancheAges={(reservationAges || []).filter(f => f.isAdulte === adulte) || []} isModeModifier={mode !== Mode.Lecture}/>
    }, [mode, dataGridStore])

    const onEditorPreparing = useCallback(async (e: any) => {
            if (e.parentType === "dataRow") {
                if (e.dataField == "idAuxiliaire") {
                    if (e.row?.data.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Produit) {
                        e.editorOptions.buttons = catalogueEditorOptions.buttons
                    }

                    e.editorOptions.dataSource = new DataSource({
                        store: new CustomStore({
                            key: "id",
                            byKey: async (key) => (e.row?.data.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite ? (unites || []) : (catalogues || [])).find(f => f.id.equals(key)),
                            load: async (o) => {
                                if (e.row?.data.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite) {
                                    const result = await getUnitesDisponibles({customPath: `typeunite/${e.row?.data.idTypeAuxiliaire}/unitedisponiblites?typeCalendrier=${TypeTabCalendrier.TypeUnite}&datedebut=${e.row?.data.dateDebut.toDateString()}&datefin=${e.row?.data.dateFin.toDateString()}`, returnOnly: true})
                                    return result;
                                } else {
                                    return catalogues;
                                }
                            }
                        }),
                        reshapeOnPush: true
                    });
                }
                if (e.dataField == "dateDebut" || e.dataField == "dateFin" || e.dataField == "nombreJours") {
                    e.editorOptions.visible = e.row?.data.typeAuxiliaire === ReservationDetailSommaireTypeAuxiliaire.Unite
                }
            }
        }, [getUnitesDisponibles, unites, catalogues, catalogueEditorOptions.buttons]
    )


    const OnAdd = useCallback(async (e: any) => {
        const {selectedUnite, selectedProduit, dateDebut, dateFin} = e.itemData as {
            selectedUnite: (ILookupItem & { quantite?: number })[],
            selectedProduit: (ICatalogue & { quantite?: number })[],
            dateDebut: Date,
            dateFin: Date,
        };
        const newReservationDetailSommaire: IReservationDetailSommaire[] = [];

        if (selectedUnite) {
            for (const unite of selectedUnite) {
                const ReservationDetailSommaire: IReservationDetailSommaire = {
                    idReservationDetailSommaire: undefined,
                    idReservationEntete: selectedReservation?.idReservationEntete || Guid.empty,
                    typeAuxiliaire: ReservationDetailSommaireTypeAuxiliaire.Unite,
                    idAuxiliaire: unite.id,
                    dateDebut: dateDebut,
                    dateFin: dateFin,
                    montant: 0,
                    dateCreation: new Date(),
                    dateModification: new Date(),
                    dateSuppression: undefined,
                    idUsagerCreation: user?.idUsager,
                    idUsagerModification: user?.idUsager,
                    idUsagerSuppression: undefined,
                    genereAuto: true,
                    idClient: selectedReservation?.reservationEntete.idClient || Guid.empty,
                    reservationDetails: [],
                    idTypeAuxiliaire: unite.idParent,
                    nombreJours: 1,
                    reservationAges: selectedReservation?.reservationAges.filter(f => f.idAuxiliaire.equals(selectedReservation?.idReservationEntete)) || [],
                    reservationDetailEquipements: {} as IReservationEquipement,
                    reservationDetailOccupants: [] as IReservationDetailOccupant[],
                    isDeleted: false

                } as IReservationDetailSommaire;

                await postReservationDetailSommaire({
                    ReservationDetailSommaire: {...ReservationDetailSommaire, idReservationEntete: selectedReservation?.idReservationEntete} as IReservationDetailSommaire,
                    IdUsager: user?.idUsager
                }, 'reservationDetailSommaire/CreateOrUpdateRow', true).then((newRow) => {
                    newReservationDetailSommaire.push(newRow)
                });

            }
        }

        if (selectedProduit) {
            for (const produit of selectedProduit) {
                const ReservationDetailSommaire: IReservationDetailSommaire = {
                    idReservationDetailSommaire: undefined,
                    idReservationEntete: selectedReservation?.idReservationEntete || Guid.empty,
                    typeAuxiliaire: ReservationDetailSommaireTypeAuxiliaire.Produit,
                    idAuxiliaire: produit.idProduit,
                    dateDebut: undefined,
                    dateFin: undefined,
                    montant: 0,
                    dateCreation: new Date(),
                    dateModification: new Date(),
                    dateSuppression: undefined,
                    idUsagerCreation: user?.idUsager,
                    idUsagerModification: user?.idUsager,
                    idUsagerSuppression: undefined,
                    genereAuto: true,
                    idClient: selectedReservation?.reservationEntete.idClient || Guid.empty,
                    reservationDetails: [],
                    idTypeAuxiliaire: Guid.max,
                    nombreJours: produit.quantite || 1,
                    reservationAges: selectedReservation?.reservationAges.filter(f => f.idAuxiliaire.equals(selectedReservation?.idReservationEntete)) || [],
                    reservationDetailEquipements: {} as IReservationEquipement,
                    reservationDetailOccupants: [] as IReservationDetailOccupant[],
                    isDeleted: false
                } as IReservationDetailSommaire;

                await postReservationDetailSommaire({
                    ReservationDetailSommaire: {...ReservationDetailSommaire, idReservationEntete: selectedReservation?.idReservationEntete} as IReservationDetailSommaire,
                    IdUsager: user?.idUsager
                }, 'reservationDetailSommaire/CreateOrUpdateRow', true).then((newRow) => {
                    newReservationDetailSommaire.push(newRow)
                });

            }
        }

        await postCalculerReservation({
            ...selectedReservation,
            reservationDetailSommaires: [...selectedReservation?.reservationDetailSommaires || [], ...newReservationDetailSommaire],
        }, "reservation/CalculerTotal", true)
            .then((reservation) => {
                setSelectedDataRow(reservation)
                const options = newReservationDetailSommaire.flatMap(m => m.optionReservations || [])
                const optionsGrouped = new Map<Guid, IOptionReservation>()
                options.forEach((option) => {
                    const existant = optionsGrouped.get(option.idAuxiliaire);
                    optionsGrouped.set(option.idAuxiliaire, {...option, quantiteGroup: (option.ajoutAutomatique ? 1 : 0) + (existant?.quantiteGroup || 0)})
                })

                setIsPopupVisibleOptions({visible: newReservationDetailSommaire.some(s => s.optionReservations && s.optionReservations.length > 0), optionReservation: Array.from(optionsGrouped).flatMap(m => m[1]), dateDebut: dateDebut, dateFin: dateFin});
            })

        setIsFieldModifier(true)

    }, [user?.idUsager, selectedReservation.reservationEntete?.idClient, selectedReservation.reservationAges, postReservationDetailSommaire, postCalculerReservation, selectedReservation, setSelectedDataRow, setIsFieldModifier]);

    const onInitNewRow = useCallback(async (e: InitNewRowEvent<IReservationDetailSommaire>) => {
        const ReservationDetailSommaire: IReservationDetailSommaire = {
            idReservationDetailSommaire: undefined,
            idReservationEntete: selectedReservation?.idReservationEntete || Guid.empty,
            typeAuxiliaire: ReservationDetailSommaireTypeAuxiliaire.Unite,
            idAuxiliaire: Guid.empty,
            dateDebut: selectedReservation?.dateArrivee,
            dateFin: selectedReservation?.dateDepart,
            montant: 0,
            dateCreation: new Date(),
            dateModification: new Date(),
            dateSuppression: undefined,
            idUsagerCreation: user?.idUsager,
            idUsagerModification: user?.idUsager,
            idUsagerSuppression: undefined,
            genereAuto: true,
            idClient: selectedReservation?.reservationEntete.idClient || Guid.empty,
            reservationDetails: [],
            idTypeAuxiliaire: Guid.empty,
            nombreJours: 1,
            reservationAges: selectedReservation?.reservationAges.filter(f => f.idAuxiliaire.equals(selectedReservation?.idReservationEntete)) || [],
            reservationDetailEquipements: {} as IReservationEquipement,
            reservationDetailOccupants: [] as IReservationDetailOccupant[],
            isDeleted: false
        } as IReservationDetailSommaire

        e.promise = postReservationDetailSommaire({
            ReservationDetailSommaire: ReservationDetailSommaire,
            idUsage: user?.idUsager
        }, 'reservationDetailSommaire/CreateOrUpdateRow', true)
            .then((newRow) => {
                e.data = newRow;
            });
    }, [selectedReservation?.idReservationEntete, selectedReservation?.dateArrivee, selectedReservation?.dateDepart, user?.idUsager, selectedReservation?.reservationEntete?.idClient, selectedReservation?.reservationAges, postReservationDetailSommaire])

    const dateArriveeDepartEditorOptions = useMemo(() => {
        return {
            useMaskBehavior: true
        }
    }, []);

    const onFocusedRowChanged = useCallback((e: FocusedRowChangedEvent<IReservationDetailSommaire>) => {
        setFocusedRow(e.row?.data)
    }, [setFocusedRow]);

    const onPopupGestionClientSelectionchanged = useCallback((selectedClient: IClient) => {
        if (mode === Mode.Lecture) return;
        setIsFieldModifier(true);
        dataGridStore.update(focusedRow?.idReservationDetailSommaire, {idClient: selectedClient.idClient});
    }, [mode, setIsFieldModifier, dataGridStore, focusedRow?.idReservationDetailSommaire]);

    const onDragStart = useCallback((e: any) => e.cancel = true, [])
    const setTypeAuxiliaireCellValue = useCallback((newData: any, value: any, currentRowData: IReservationDetailSommaire) => {
        newData.typeAuxiliaire = value === Guid.max ? 'PR' : 'UN'
        newData.idTypeAuxiliaire = value
    }, []);
    const lookupTypeAuxiliairedataSource = useMemo(() => [...(typeUnites || []).filter((f => !f.id.equals(Guid.max))), {id: Guid.max, description: "Produit"}], [typeUnites]);

    const adultesCellRender = useCallback((e: any) => adulteCellRender({...e, adulte: true}), [adulteCellRender]);
    const enfantsCellRender = useCallback((e: any) => adulteCellRender({...e, adulte: false}), [adulteCellRender]);

    const typeAuxiliaireGroupeCellComponent = useCallback((e: any) => {
        return <div>{e.data.value === 'PR' ? 'Produits' : 'Unités'}</div>
    }, []);

    const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);
    const reservationDetailComponent = useCallback((e: any) => {
        return reservationDetailView({...e, selectedReservation, isModeModifier: mode !== Mode.Lecture, setSelectedDataRow, dataGridStore})
    }, [selectedReservation, mode, setSelectedDataRow, dataGridStore]);

    const uniteProduitSetCellValue = useCallback((newData: IReservationDetailSommaire, value: any, currenRowData: IReservationDetailSommaire) => {
        newData.idAuxiliaire = value
    }, []);

    useEffect(() => {
        if (focusedRow) {
            const refreshedRowSelected = selectedReservation.reservationDetailSommaires.find(f => f.idReservationDetailSommaire?.equals(focusedRow.idReservationDetailSommaire))
            setFocusedRow(refreshedRowSelected)
        }

    }, [selectedReservation, focusedRow]);

    const onCloseOptionPopup = useCallback(async (optionSelected: IOptionReservation[]) => {
        setIsPopupVisibleOptions({visible: false})

        const selectedProduit = optionSelected.filter(option => option.typeAuxiliaire === TypeAuxiliaire.Produit).map(option => {
            let quantite: number = option.quantite || 1;
            switch (option.modeCalcul) {
                case OptionReservationModeCalcul.Duree:
                    quantite = focusedRow?.dateDebut && focusedRow?.dateFin ? differenceInDays(focusedRow?.dateFin, focusedRow?.dateDebut) : 0;
                    break;
                case OptionReservationModeCalcul.DureePersonne:
                    const nbPersonne: number = (focusedRow?.reservationAges || []).map(({nombrePersonnes}) => nombrePersonnes).reduce((total, nombrePersonnes) => total + nombrePersonnes || 0) || 0;
                    quantite = (focusedRow?.dateDebut && focusedRow?.dateFin ? differenceInDays(focusedRow?.dateFin, focusedRow?.dateDebut) : 0) * nbPersonne;
                    break;
                case OptionReservationModeCalcul.Global:
                    break;
                case OptionReservationModeCalcul.Gratuite:
                    quantite = 0;
                    break;
            }

            return {
                idProduit: option.idAuxiliaire,
                quantite: quantite * (option.quantiteGroup || 1)
            };
        })

        const selectedUnite = optionSelected.filter(option => option.typeAuxiliaire === TypeAuxiliaire.Unite).map(option => ({
            id: option.idAuxiliaire,
            quantite: option.quantite
        }));

        const OptionReservationsSelected = optionSelected.filter(option => option.typeAuxiliaire === TypeAuxiliaire.TypeUnite);
        const typeUniteSelected = await Promise.all(OptionReservationsSelected.map(async (option) => {
            const result = await getUnitesDisponibles({customPath: `typeunite/${option?.idAuxiliaire}/unitedisponiblites?typeCalendrier=${TypeTabCalendrier.TypeUnite}&datedebut=${isPopupVisibleOptions.dateDebut?.toDateString()}&datefin=${isPopupVisibleOptions.dateFin?.toDateString()}`, returnOnly: true})
            for (let i = 0; i < (option.quantite || 0); i++) {
                selectedUnite.push({id: result[i].id, quantite: 1});
            }
        }))

        await OnAdd({itemData: {selectedUnite: selectedUnite, selectedProduit: selectedProduit || [], dateDebut: isPopupVisibleOptions.dateDebut, dateFin: isPopupVisibleOptions.dateFin}});

    }, [focusedRow?.reservationAges, OnAdd, isPopupVisibleOptions.dateDebut, isPopupVisibleOptions.dateFin, getUnitesDisponibles, focusedRow?.dateDebut, focusedRow?.dateFin]);

    return (<>
        <DataGrid id={"grid-detail-sommaire"}
                  className={"detail-reservation"}
                  ref={dataGridRef}
                  repaintChangesOnly={true}
                  dataSource={dataGridDetailDataSource}
                  allowColumnResizing={true}
                  columnResizingMode={"nextColumn"}
                  onEditorPreparing={onEditorPreparing}
                  onInitNewRow={onInitNewRow}
                  focusedRowEnabled={true}
                  height={"inherit"}
                  key={"grid-detail-sommaire"}
                  remoteOperations={false}
                  filterValue={dataGridFilterValue}
                  onFocusedRowChanged={onFocusedRowChanged}
                  focusedRowKey={focusedRow?.idReservationDetailSommaire}
                  customizeColumns={customizeColumns}
        >
            <Toolbar>
                <Item widget={"dxButton"} location={"before"} options={ajouterButtonOtions}/>
                <Item widget={"dxButton"} location={"before"} options={effacerButtonOtions}/>
                <Item widget={"dxButton"} location={"before"} options={bloquerButtonOtions}>
                    <Button text={"Bloquer"} stylingMode={"text"} onClick={() => {

                    }} disabled={mode === Mode.Lecture}>
                        <IonIcon className={"dx-icon"} icon={banOutline}></IonIcon>
                        <span className="dx-button-text">Bloquer</span>
                    </Button>
                </Item>
                <Item widget={"dxButton"} location={"before"} disabled={!focusedRow || focusedRow.typeAuxiliaire !== ReservationDetailSommaireTypeAuxiliaire.Unite} options={occupantsButtonOtions} cssClass={occupantsCount || 0 > 0 ? 'bold' : ''}/>
                <Item widget={"dxButton"} location={"before"} disabled={!focusedRow || focusedRow.typeAuxiliaire !== ReservationDetailSommaireTypeAuxiliaire.Unite} options={equipementButtonOtions} cssClass={`btn-equipement${hasEquipementSelected ? ' bold' : ''}`}/>
                <Item widget={"dxButton"} location={"before"} disabled={!focusedRow || mode === Mode.Lecture || !focusedRow?.optionReservations || focusedRow.optionReservations && focusedRow?.optionReservations?.length === 0} options={optionButtonOtions}/>
            </Toolbar>
            <RowDragging onDragStart={onDragStart} group={"ajoutRapid"} allowReordering={false} showDragIcons={false} allowDropInsideItem={mode !== Mode.Lecture} onAdd={OnAdd}/>
            <Grouping autoExpandAll={true} allowCollapsing={false}/>
            <Editing allowAdding={mode !== Mode.Lecture} allowDeleting={true} allowUpdating={mode !== Mode.Lecture} newRowPosition={"first"} mode={"cell"} startEditAction={"click"}/>
            <Scrolling showScrollbar={"always"}/>
            <Paging enabled={false}/>
            <Column sortOrder={"desc"} dataType={"object"} groupCellComponent={typeAuxiliaireGroupeCellComponent} dataField={"typeAuxiliaire"} groupIndex={0} visible={false}/>
            <Column dataField={"idTypeAuxiliaire"} dataType={"object"} caption={"Type unité"} width={"15%"} setCellValue={setTypeAuxiliaireCellValue}>
                <Lookup valueExpr={"id"} displayExpr={"description"} dataSource={lookupTypeAuxiliairedataSource}/>
            </Column>
            <Column caption={"Date"} alignment={"center"}>
                <Column caption={"Arrivée"} dataType={"datetime"} width={"25%"} dataField={"dateDebut"} format={"dd-MM-yyyy HH:mm"} editorOptions={dateArriveeDepartEditorOptions}/>
                <Column caption={"Départ"} dataType={"datetime"} width={"25%"} dataField={"dateFin"} format={"dd-MM-yyyy HH:mm"} editorOptions={dateArriveeDepartEditorOptions}/>
            </Column>
            <Column dataType={"number"} caption={"Durée"} width={"6%"} alignment={"center"} dataField={"nombreJours"}/>
            <Column dataType={"number"} allowEditing={false} caption={"Adulte(s)"} width={"10%"} cellRender={adultesCellRender}/>
            <Column dataType={"number"} allowEditing={false} caption={"Enfant(s)"} width={"10%"} cellRender={enfantsCellRender}/>
            <Column
                width={"30%"}
                caption={"Unités / Produits"}
                dataField={"idAuxiliaire"}
                dataType={"object"}
                alignment={"center"}
                cellComponent={uniteProduitCellComponent}
                setCellValue={uniteProduitSetCellValue}
            >
                <Lookup valueExpr={"id"} displayExpr={"description"}/>
            </Column>
            <Column caption={"Client"} alignment={"center"} width={"30%"} dataType={"object"} dataField={"idClient"} cellRender={ClientSelectBox} editCellRender={ClientSelectBox}/>
            <Column dataField={"dateSuppression"} visible={false} dataType={"datetime"}/>
            <Column dataField={"idUsagerSuppression"} visible={false} dataType={"object"}/>
            <Column dataField={"isDeleted"} visible={false} dataType={"boolean"}/>
            <Column allowEditing={false} caption={"Montant"} width={"15%"} dataType="number" format={"#0.00 $"} dataField={"montant"}/>
            <Column type="buttons" width={32}/>
            <MasterDetail enabled={true} component={reservationDetailComponent}/>
        </DataGrid>
        <PopupGestionClient isModeSelection={mode !== Mode.Lecture} onSelectionchanged={onPopupGestionClientSelectionchanged}/>
        <PopupPage<IReservationDetailOccupant[]>
            titre="Liste des occupants"
            mode={"ajout"}
            height={"60%"}
            width={"60%"}
            isPopupOpen={isPopupVisibleOccupant}
            onSelection={(value) => {
                const reservationDetailSommaireFiltered = [...(selectedReservation?.reservationDetailSommaires.filter(f => !f.idReservationDetailSommaire?.equals(focusedRow?.idReservationDetailSommaire)) || [])];
                dataSource?.store().update(selectedReservation.idReservationEntete, {...selectedReservation, reservationDetailSommaires: [...reservationDetailSommaireFiltered, {...focusedRow, reservationDetailOccupants: value}]});
                setIsFieldModifier(true);
                setPopupVisibilityOccupant(false)
            }}
            onClose={(value) => {
                setPopupVisibilityOccupant(false)
            }}
        >
            <PopUpOccupant reservationFormDataSource={reservationFormDataSource} reservationDetailSommaireParent={focusedRow}/>
        </PopupPage>
        {isPopupEquipementOuvert && <PopupEquipement key={`${dataGridRef?.current?.instance().option("focusedRowKey")}-popup-equipement`}
                                                     isVisible={true}
                                                     idUnite={focusedRow?.idAuxiliaire || Guid.empty}
                                                     togglePopup={() => setIsPopupEquipementOuvert(false)}
                                                     equipement={focusedRow?.reservationDetailEquipements}
                                                     onValueChanged={() => {
                                                         setIsFieldModifier(true)
                                                     }}
                                                     isModeModifier={mode !== Mode.Lecture}/>}
        <CataloguePopGestion>
            <GestionCatalogueProduits/>
        </CataloguePopGestion>

        {isPopupVisibleOptions?.visible ? <PopupOptions onClose={onCloseOptionPopup} optionsReservation={isPopupVisibleOptions.optionReservation || []}/> : null}

        <MessageBox title={"Attention"} message={"Une ou plusieurs lignes du détail ont été modifiées.\n Les modifications apportées seront annulées, voulez-vous continuer"} buttonType={"OuiNon"} onConfirme={() => {
            if (isMessageBoxOpen)
                recalculeReservationSommaire(isMessageBoxOpen, true);
            setIsMessageBoxOpen(undefined)
        }} onAnnuler={() => {
            setIsMessageBoxOpen(undefined)
        }} isOpen={isMessageBoxOpen !== undefined}/>
    </>);
};

export default ReservationDetailSommaireGrid;